'use client';

import { c as _c } from "react/compiler-runtime";
import { SpendRangeBar } from '@/domains/pages/ai-spend/components/spend-range-bar';
import { AVG_SPEND_PER_TASK_DESCRIPTION, SEGMENT_SPEND_PER_TASK_TOOLTIP } from '@/domains/pages/ai-spend/constants';
import type { AiSpendOrgSnapshot } from '@/domains/pages/ai-spend/utils/map-ai-spend-aggregates';
import type { SegmentSpendSummary, SpendPerTaskRange } from '@/domains/pages/ai-spend/utils/map-ai-spend-segments';
import { NebulyPageCard } from '@/domains/pages/shared/nebuly-page-card';
import type { SegmentKind } from '@/domains/pages/shared/segment-kind';
import { segmentKindLabel } from '@/domains/pages/shared/segment-kind';
import { PAGE_KPI_NUMBER_CLASS } from '@/domains/pages/shared/typography';
import { formatSpend } from '@/utils/format.util';
import { cn } from '@/utils/shad';
type SegmentSpendPerTaskCardProps = {
  kind: SegmentKind;
  selected: SegmentSpendSummary | null;
  selectedName: string;
  org: AiSpendOrgSnapshot;
  spendPerTaskRank: number;
  spendPerTaskRange: SpendPerTaskRange;
  segmentCount: number;
  formatSegmentName?: (name: string) => string;
  className?: string;
};
export function SegmentSpendPerTaskCard(t0) {
  const $ = _c(29);
  const {
    kind,
    selected,
    selectedName,
    org,
    spendPerTaskRank,
    spendPerTaskRange,
    segmentCount,
    formatSegmentName,
    className
  } = t0;
  let t1;
  if ($[0] !== kind) {
    t1 = segmentKindLabel(kind);
    $[0] = kind;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const segmentNoun = t1;
  let t2;
  if ($[2] !== kind) {
    t2 = segmentKindLabel(kind, true);
    $[2] = kind;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const segmentNounPlural = t2;
  const rankTotal = spendPerTaskRange.rankedCount || segmentCount;
  let t3;
  if ($[4] !== className) {
    t3 = cn("min-h-0", className);
    $[4] = className;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const t4 = selected?.spendPerTask ?? null;
  let t5;
  if ($[6] !== t4) {
    t5 = formatSpend(t4, {
      precision: "exact"
    });
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== selected) {
    t6 = selected?.spendPerTask != null ? <>{formatSpend(selected.spendPerTask)}<span className="text-2xl text-muted-foreground">/task</span></> : "\u2014";
    $[8] = selected;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t5 || $[11] !== t6) {
    t7 = <span className={PAGE_KPI_NUMBER_CLASS} title={t5}>{t6}</span>;
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <p className="text-sm leading-snug text-muted-foreground">{AVG_SPEND_PER_TASK_DESCRIPTION}</p>;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const t9 = selected?.spendPerTask ?? null;
  let t10;
  if ($[14] !== formatSegmentName || $[15] !== org.spendPerTask || $[16] !== rankTotal || $[17] !== segmentNoun || $[18] !== segmentNounPlural || $[19] !== selectedName || $[20] !== spendPerTaskRange.cheapest || $[21] !== spendPerTaskRange.mostExpensive || $[22] !== spendPerTaskRank || $[23] !== t9) {
    t10 = <SpendRangeBar className="mt-3" selectedName={selectedName} selectedValue={t9} cheapest={spendPerTaskRange.cheapest} mostExpensive={spendPerTaskRange.mostExpensive} orgAverage={org.spendPerTask} segmentNoun={segmentNoun} segmentNounPlural={segmentNounPlural} rank={spendPerTaskRank} rankTotal={rankTotal} formatSegmentName={formatSegmentName} />;
    $[14] = formatSegmentName;
    $[15] = org.spendPerTask;
    $[16] = rankTotal;
    $[17] = segmentNoun;
    $[18] = segmentNounPlural;
    $[19] = selectedName;
    $[20] = spendPerTaskRange.cheapest;
    $[21] = spendPerTaskRange.mostExpensive;
    $[22] = spendPerTaskRank;
    $[23] = t9;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== t10 || $[26] !== t3 || $[27] !== t7) {
    t11 = <NebulyPageCard title="Avg. AI spend per task" description={SEGMENT_SPEND_PER_TASK_TOOLTIP} className={t3} contentClassName="flex flex-col gap-3 !py-3" testId="segment-avg-spend-per-task-card">{t7}{t8}{t10}</NebulyPageCard>;
    $[25] = t10;
    $[26] = t3;
    $[27] = t7;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  return t11;
}